import axios, {
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { jwtDecode } from "jwt-decode";

import { storage } from "../Utils/authStorage";
import { MyTokenPayload } from "./JwtToken";


/* ===========================
   API BASE URL
=========================== */
export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:8080/api/v1";

/* ===========================
   BACKEND ROOT URL (no /api/v1 suffix)
=========================== */
export const BACKEND_BASE_URL =
  API_BASE_URL.replace(/\/api\/v1\/?$/, "");


/* ===========================
   Websocket BASE URL
=========================== */
export const WS_BASE_URL =
process.env.REACT_APP_WS_BASE_URL ||
  "http://localhost:8080";


/* ===========================
   AXIOS INSTANCE
=========================== */
const ApiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});


/* ===========================
   SESSION REFRESH
   =========================== */

/** How long before the access token expires the refresh is scheduled. */
const REFRESH_LEEWAY_MS = 60_000;

/** Never schedule a refresh sooner than this, so a just-issued token is not churned. */
const MIN_REFRESH_DELAY_MS = 5_000;

/** How many times a transient refresh failure is retried before the session is given up on. */
const MAX_REFRESH_ATTEMPTS = 3;

/** Base back-off between refresh retries. */
const REFRESH_RETRY_DELAY_MS = 400;

/** How long to wait before trying the scheduled refresh again after a transient failure. */
const PROACTIVE_RETRY_DELAY_MS = 30_000;

/** How often the scheduled refresh may be retried after a transient failure. */
const MAX_PROACTIVE_RETRIES = 5;

/**
 * The single refresh call that is currently in flight. Every 401 that arrives while a refresh is
 * running waits for that same call instead of starting another one. Without this, a dashboard
 * that fires several requests at once produced one refresh call per request, which is how the
 * refresh endpoint ran into its rate limit and signed the user out.
 */
let refreshInFlight: Promise<string> | null = null;

/** Timer for the scheduled refresh that runs just before the access token expires. */
let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

/** How often the scheduled refresh has been retried after a transient failure. */
let proactiveRetryCount = 0;

/** Notified when the session really is over, so the UI can say so instead of a silent redirect. */
let sessionExpiredHandler: (() => void) | null = null;

/**
 * Set once the session has ended. While it is set no further refresh is attempted, so a dead
 * session does not keep hammering the refresh endpoint from every request that comes back 401.
 */
let sessionEnded = false;

/**
 * Raised when the refresh token itself has been rejected: the session is genuinely over.
 * Distinct from a network failure or a server error, which are worth retrying.
 */
export class SessionExpiredError extends Error {
  constructor(message = "Session expired") {
    super(message);
    this.name = "SessionExpiredError";
  }
}

/**
 * Registers the callback that runs when the session ends. Returns a function that unregisters it.
 */
export const onSessionExpired = (handler: (() => void) | null): (() => void) => {
  sessionExpiredHandler = handler;

  return () => {
    if (sessionExpiredHandler === handler) {
      sessionExpiredHandler = null;
    }
  };
};

const clearProactiveRefreshTimer = () => {
  if (proactiveRefreshTimer !== null) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
};

const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Reads the expiry of an access token.
 */
const readTokenExpiryMs = (token: string): number | null => {
  try {
    const payload = jwtDecode<MyTokenPayload>(token);

    if (!payload?.exp) {
      return null;
    }

    return payload.exp * 1000;

  } catch {
    return null;
  }
};

/**
 * Schedules a refresh that runs shortly before the access token expires.
 *
 * This is what keeps a user who is actively working in the app signed in: the token is replaced
 * while it is still valid, so no request ever has to fail with a 401 and no race between several
 * requests can start a refresh at the same time.
 */
const scheduleProactiveRefresh = (token: string) => {
  clearProactiveRefreshTimer();
  proactiveRetryCount = 0;

  const expiryMs = readTokenExpiryMs(token);

  if (!expiryMs) {
    return;
  }

  const delayMs = Math.max(
    MIN_REFRESH_DELAY_MS,
    expiryMs - Date.now() - REFRESH_LEEWAY_MS
  );

  proactiveRefreshTimer = setTimeout(runProactiveRefresh, delayMs);
};

/**
 * Runs the scheduled refresh. A failure that could be a blip is retried a few times so the
 * token is renewed without waiting for the user's next action.
 */
const runProactiveRefresh = () => {
  proactiveRefreshTimer = null;

  refreshAccessToken().catch((error) => {
    if (error instanceof SessionExpiredError) {
      endSession();
      return;
    }

    if (proactiveRetryCount >= MAX_PROACTIVE_RETRIES) {
      return;
    }

    proactiveRetryCount += 1;

    proactiveRefreshTimer = setTimeout(
      runProactiveRefresh,
      PROACTIVE_RETRY_DELAY_MS
    );
  });
};

/**
 * A refresh failure is worth retrying when the server was unreachable, overloaded or rate
 * limiting us. Those are not a reason to end the session.
 */
const isTransientRefreshFailure = (error: unknown): boolean => {
  const status = (error as AxiosError)?.response?.status;

  if (status === undefined || status === null) {
    // No response at all: the request never reached the server.
    return true;
  }

  return status === 408 || status === 429 || status >= 500;
};

const postRefresh = async (): Promise<string> => {
  let response;

  try {
    response = await axios.post(
      `${API_BASE_URL}/auth/refresh`,
      {},
      {
        withCredentials: true,
      }
    );

  } catch (error) {
    // The server rejected the refresh token itself: the session is over and retrying is futile.
    if (!isTransientRefreshFailure(error)) {
      throw new SessionExpiredError("Refresh token rejected");
    }

    throw error;
  }

  const authData = response.data.data;

  storage.setToken(authData.accessToken);

  storage.setUser({
    id: authData.userId,
    role: authData.role,
  });

  return authData.accessToken;
};

/**
 * Exchanges the refresh cookie for a new access token.
 *
 * All callers share the same in-flight request, and a failure that could be a blip is retried
 * before the session is given up on.
 */
export const refreshAccessToken = async (): Promise<string> => {
  if (sessionEnded) {
    throw new SessionExpiredError("Session has ended");
  }

  if (refreshInFlight) {
    return refreshInFlight;
  }

  const attempt = (async () => {
    let lastError: unknown;

    for (let index = 0; index < MAX_REFRESH_ATTEMPTS; index++) {
      try {
        return await postRefresh();

      } catch (error) {
        lastError = error;

        if (!isTransientRefreshFailure(error)) {
          break;
        }

        await delay(REFRESH_RETRY_DELAY_MS * (index + 1));
      }
    }

    throw lastError;
  })();

  refreshInFlight = attempt;

  const release = () => {
    if (refreshInFlight === attempt) {
      refreshInFlight = null;
    }
  };

  attempt.then(release, release);

  return attempt;
};

/**
 * Ends the session. The UI is told first, so the user gets an explanation instead of the page
 * silently jumping to the login screen.
 */
const endSession = () => {
  if (sessionEnded) {
    return;
  }

  sessionEnded = true;
  clearProactiveRefreshTimer();

  if (sessionExpiredHandler) {
    // The overlay explains what happened and offers a way back in. The current page stays put
    // so the user is not thrown to the login screen with no explanation.
    sessionExpiredHandler();
    return;
  }

  storage.clearAuth();

  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

/**
 * Marks the session as over without telling the user about it.
 *
 * <p>Used when the app starts without a session at all - a first visit, or a return after the
 * browser dropped the refresh cookie. There is nothing to explain in that case: the login screen
 * is the explanation. It also stops every later request from starting a refresh that is bound to
 * fail.</p>
 */
export const markSessionEnded = () => {
  sessionEnded = true;
  clearProactiveRefreshTimer();
};

/* ===========================
   LOGOUT FUNCTION
=========================== */
export const logoutUser = async () => {

  try {
    await ApiClient.post("/auth/logout");
  } catch (error) {
    console.error("Logout API failed", error);
  }

  sessionEnded = true;
  storage.clearAuth();
  clearProactiveRefreshTimer();

  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};


/* ===========================
   REQUEST INTERCEPTOR
=========================== */
ApiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {

    const token = storage.getToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },

  (error) => Promise.reject(error)
);

/* ===========================
   RESPONSE INTERCEPTOR
=========================== */
ApiClient.interceptors.response.use(
  (response) => response,

  async (error) => {

    const originalRequest = error.config as
      (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    // The refresh call itself failed. There is nothing to retry here and no reason to redirect
    // a second time - the caller of refreshAccessToken decides what happens next.
    if (
      originalRequest?.url?.includes("/auth/refresh")
    ) {
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !sessionEnded
    ) {

      originalRequest._retry = true;

      try {

        const newToken =
          await refreshAccessToken();

        originalRequest.headers.Authorization =
          `Bearer ${newToken}`;

        return ApiClient(originalRequest);

      } catch (refreshError) {

        const isExpired =
          refreshError instanceof SessionExpiredError ||
          !isTransientRefreshFailure(refreshError);

        if (isExpired) {
          endSession();
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Every new access token - whether it came from a sign-in or from a refresh - re-arms the
 * scheduled refresh.
 */
window.addEventListener("auth-token-changed", (event) => {
  const token = (event as CustomEvent<{ token?: string | null }>)?.detail?.token;

  if (token) {
    // A fresh token means a live session again.
    sessionEnded = false;
    scheduleProactiveRefresh(token);
  }
});

export default ApiClient;
